/*
 * The widths of the columns of a table from the texts of their cells: each column gets the
 * length that fits 80 % of its texts (the longest ones wrap inside the cell), at least that of
 * the longest word of its header, so the widths follow the content without a few long values
 * taking the whole table.
 */

// The share of the texts of a column that fit on one line
const FITTING = 0.8;
// A column is never narrower or wider than this many characters
const MIN_LENGTH = 4;
const MAX_LENGTH = 40;

// The length of a text as average characters: the digits and the capitals are wider
const WIDE_CHARACTER = 1.15;
const textLength = (text: string): number =>
  Array.from(text).reduce(
    (sum, character) =>
      sum + (/[0-9A-ZÅÄÖ@]/.test(character) ? WIDE_CHARACTER : 1),
    0
  );

const lengthFitting = (lengths: number[]): number => {
  if (lengths.length === 0) {
    return 0;
  }
  const sorted = [...lengths].sort((a, b) => a - b);
  const index = Math.min(
    sorted.length - 1,
    Math.ceil(sorted.length * FITTING) - 1
  );
  return sorted[Math.max(0, index)];
};

/**
 * The widths of the columns in characters, in the order of the columns.
 * texts: the shown texts of the cells, a list per column; headers: the names of the columns.
 */
export const getColumnLengths = (
  texts: string[][],
  headers: string[]
): number[] =>
  texts.map((column, index) => {
    const header = Math.max(
      ...(headers[index] ?? '').split(/\s+/).map((word) => word.length)
    );
    const content = lengthFitting(column.map((text) => textLength(text ?? '')));
    return Math.min(MAX_LENGTH, Math.max(MIN_LENGTH, header, content));
  });

/** The widths of the columns as percentages of the table, in the order of the columns. */
const getColumnWidths = (texts: string[][], headers: string[]): number[] => {
  const lengths = getColumnLengths(texts, headers);
  const total = lengths.reduce((sum, length) => sum + length, 0) || 1;

  return lengths.map((length) => (length / total) * 100);
};

interface IColumn {
  dataField: string;
  formatter?: (cell: any, row: any) => unknown;
  text: string;
}

// The padding of a cell, in characters (with room for the digits, wider than the letters)
const CELL_PADDING = 3;
// The width of an average character of the text of a table, in em (narrower than "ch",
// the width of the digit 0)
const CHARACTER_EM = 0.48;

/**
 * The columns of a react-bootstrap-table with their widths in characters from the shown texts
 * of the rows (see getColumnLengths), and the width the table needs: on a wider screen the
 * columns share the extra width, on a narrower one the table scrolls. A cell shows its value
 * through the formatter of its column.
 */
export const withContentWidths = <T extends IColumn>(
  columns: T[],
  rows: Record<string, any>[]
): {
  columns: (T & { headerStyle: { width: string } })[];
  minWidth: string;
} => {
  const texts = columns.map((column) =>
    rows.map((row) => {
      const shown = column.formatter
        ? column.formatter(row[column.dataField], row)
        : row[column.dataField];
      return shown === null || shown === undefined ? '' : String(shown);
    })
  );
  const lengths = getColumnLengths(
    texts,
    columns.map((column) => column.text)
  ).map((length) => length + CELL_PADDING);
  const total = lengths.reduce((sum, length) => sum + length, 0) || 1;

  // Shares of the table, which is at least as wide as the characters of its columns
  return {
    columns: columns.map((column, index) => ({
      ...column,
      headerStyle: { width: `${((lengths[index] / total) * 100).toFixed(1)}%` },
    })),
    minWidth: `${(total * CHARACTER_EM).toFixed(1)}em`,
  };
};

export default getColumnWidths;
